import React, { useEffect, useMemo, useState } from "react";
import { format, startOfYear, endOfYear, eachDayOfInterval, startOfMonth, endOfMonth, addMonths, isSameMonth, subMonths, isToday } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useRegistrationIntensity } from "../../hooks/useRegistrationIntensity";

// Helper function to get intensity level and color
const getIntensityLevel = (registrations: number) => {
  if (registrations === 0) return { level: 0, color: 'bg-gray-200', tooltip: 'No registrations' };
  if (registrations <= 3) return { level: 1, color: 'bg-green-200', tooltip: 'Low activity (1-20)' };
  if (registrations <= 10) return { level: 2, color: 'bg-green-400', tooltip: 'Light activity (21-40)' };
  if (registrations <= 20) return { level: 3, color: 'bg-green-600', tooltip: 'Moderate activity (41-60)' };
  if (registrations <= 50) return { level: 4, color: 'bg-green-800', tooltip: 'High activity (61-80)' };
  return { level: 5, color: 'bg-green-900', tooltip: 'Very high activity (80+)' };
};

function ContributionDay({ date, registrations, isCurrentMonth }: { 
  date: Date; 
  registrations: number; 
  isCurrentMonth: boolean;
}) {
  const intensity = getIntensityLevel(registrations);
  const isTodayDate = isToday(date);
  
  return (
    <div
      className={`
        relative w-3 h-3 rounded-sm cursor-pointer transition-all duration-200 transform hover:scale-110
        ${intensity.color}
        ${!isCurrentMonth ? 'opacity-30' : ''}
        ${isTodayDate ? 'ring-2 ring-blue-500 ring-offset-1' : ''}
      `}
      title={`${format(date, 'MMM dd, yyyy')}: ${registrations} registrations`}
    >
      {isTodayDate && (
        <div className="absolute inset-0 rounded-sm border-2 border-blue-500"></div>
      )}
    </div>
  );
}

function RegistrationIntensity() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  const currentYear = selectedDate.getFullYear();
  const { data: intensityData, isLoading, error } = useRegistrationIntensity(currentYear);
  
  // Get year start and end for the selected date's year
  const yearStart = startOfYear(selectedDate);
  const yearEnd = endOfYear(selectedDate);
  
  // Generate all days in the year
  const yearDays = eachDayOfInterval({ start: yearStart, end: yearEnd });
  
  // Group days by weeks
  const weeks: (Date | null)[][] = [];
  let currentWeek: (Date | null)[] = [];
  
  yearDays.forEach((day, index) => {
    currentWeek.push(day);
    
    // Start new week on Sunday (day 0) or if it's the last day
    if (day.getDay() === 0 || index === yearDays.length - 1) {
      // Pad week to have 7 days
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push([...currentWeek]);
      currentWeek = [];
    }
  });
  
  // Get month labels
  const monthLabels = useMemo(() => {
    const labels = [];
    let currentMonth = startOfYear(selectedDate);
    
    for (let i = 0; i < 12; i++) {
      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);
      const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
      
      // Find which week this month starts in
      const firstDayOfWeek = monthStart.getDay();
      const weekIndex = Math.floor((monthDays[0].getTime() - yearStart.getTime()) / (7 * 24 * 60 * 60 * 1000));
      
      labels.push({
        name: format(currentMonth, 'MMM'),
        weekIndex: Math.max(0, weekIndex),
        monthIndex: i
      });
      
      currentMonth = addMonths(currentMonth, 1);
    }
    
    return labels;
  }, [selectedDate]);

  const monthSpans = useMemo(() => {
    const spans: { name: string; span: number }[] = [];

    let currentMonth = -1;
    let currentSpan = 0;

    weeks.forEach((week) => {
      // Find first valid day in the week
      const firstDay = week.find((d) => d !== null);

      if (!firstDay) return;

      const month = firstDay.getMonth();

      if (month !== currentMonth) {
        if (currentSpan > 0) {
          spans.push({
            name: format(new Date(selectedDate.getFullYear(), currentMonth), "MMM"),
            span: currentSpan,
          });
        }
        currentMonth = month;
        currentSpan = 1;
      } else {
        currentSpan++;
      }
    });

    // Push final month
    if (currentSpan > 0) {
      spans.push({
        name: format(new Date(selectedDate.getFullYear(), currentMonth), "MMM"),
        span: currentSpan,
      });
    }

    return spans;
  }, [weeks, selectedDate]);

  // Get registrations for a specific date
  const getRegistrationsForDate = (date: Date) => {
    if (!intensityData?.data?.heatmap) return 0;
    const dateStr = format(date, 'yyyy-MM-dd');
    return intensityData.data.heatmap[dateStr] || 0;
  };
  
  const navigateYear = (direction: 'prev' | 'next') => {
    setSelectedDate(prev => {
      if (direction === 'prev') {
        return subMonths(prev, 12);
      } else {
        return addMonths(prev, 12);
      }
    });
  };
  
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setShowDatePicker(false);
  };
  
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-lg">Registration Intensity</h3>
            <p className="text-xs text-gray-500">Registration graph for {format(selectedDate, 'yyyy')}</p>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="p-1 bg-gray-100 rounded animate-pulse"></div>
            <div className="px-3 py-1 text-sm font-medium border border-gray-300 rounded bg-gray-100 animate-pulse"></div>
            <div className="p-1 bg-gray-100 rounded animate-pulse"></div>
          </div>
        </div>
        
        {/* Month labels skeleton */}
        <div className="flex ml-10 mb-2 text-xs text-gray-500">
          {[...Array(12)].map((_, idx) => (
            <div
              key={idx}
              className="text-center bg-gray-100 animate-pulse h-4 rounded"
              style={{ width: 48 }}
            ></div>
          ))}
        </div>

        {/* Contribution grid skeleton */}
        <div className="flex gap-1 overflow-x-auto">
          {/* Day labels skeleton */}
          <div className="flex flex-col gap-1 mr-2">
            {['M', 'T', 'W', 'T', 'F','S', 'S'].map((day, index) => (
              <div key={index} className="h-3 w-3 text-xs text-gray-400 flex items-center justify-center">
                {day}
              </div>
            ))}
          </div>
          
          {/* Weeks skeleton */}
          <div className="flex gap-1">
            {[...Array(53)].map((_, weekIndex) => (
              <div key={weekIndex} className="flex flex-col gap-1">
                {[...Array(7)].map((_, dayIndex) => (
                  <div key={`skeleton-${weekIndex}-${dayIndex}`} className="w-3 h-3 bg-gray-200 rounded-sm animate-pulse"></div>
                ))}
              </div>
            ))}
          </div>
        </div>
        
        {/* Legend skeleton */}
        <div className="flex items-center gap-4 mt-4 text-xs text-gray-600">
          <span className="bg-gray-100 animate-pulse h-4 w-8 rounded"></span>
          <div className="flex gap-1">
            <div className="w-3 h-3 bg-gray-200 rounded-sm animate-pulse"></div>
            <div className="w-3 h-3 bg-gray-200 rounded-sm animate-pulse"></div>
            <div className="w-3 h-3 bg-gray-200 rounded-sm animate-pulse"></div>
            <div className="w-3 h-3 bg-gray-200 rounded-sm animate-pulse"></div>
            <div className="w-3 h-3 bg-gray-200 rounded-sm animate-pulse"></div>
          </div>
          <span className="bg-gray-100 animate-pulse h-4 w-8 rounded"></span>
        </div>
        
        {/* Stats skeleton */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {[...Array(4)].map((_, idx) => (
              <div key={idx} className="bg-gray-100 animate-pulse h-8 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-500">Error loading registration intensity data</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-lg">Registration Intensity</h3>
          <p className="text-xs text-gray-500">Registration graph for {format(selectedDate, 'yyyy')}</p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigateYear('prev')}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Previous year"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="px-3 py-1 text-sm font-medium border border-gray-300 rounded hover:bg-gray-50 transition-colors"
          >
            {format(selectedDate, 'yyyy')}
          </button>
          
          <button
            onClick={() => navigateYear('next')}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Next year"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Date Picker Modal */}
      {showDatePicker && (
        <div className="absolute z-50 mt-2">
          <DatePicker
            selected={selectedDate}
            onChange={(date: Date | null) => date && handleDateSelect(date)}
            inline
            showYearPicker
            maxDate={new Date()}
            calendarClassName="shadow-lg border border-gray-200 rounded-lg"
          />
        </div>
      )}
      
      {/* Month labels */}
      <div className="flex ml-10 mb-2 text-xs text-gray-500">
        {monthSpans.map((m, idx) => (
          <div
            key={idx}
            className="text-center"
            style={{ width: m.span * 16 }} // 16px = (w-3 + gap-1)
          >
            {m.name}
          </div>
        ))}
      </div>

      {/* Contribution grid */}
      <div className="flex gap-1 overflow-x-auto">
        {/* Day labels */}
        <div className="flex flex-col gap-1 mr-2">
          {/* <div className="h-3 "></div> */}
          {['M', 'T', 'W', 'T', 'F','S', 'S'].map((day, index) => (
            <div key={index} className="h-3  w-3 text-xs text-gray-400 flex items-center justify-center">
              {day}
            </div>
          ))}
        </div>
        
        {/* Weeks */}
        <div className="flex gap-1">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="flex flex-col gap-1">
              {week.map((day, dayIndex) => {
                if (!day) {
                  return <div key={`empty-${weekIndex}-${dayIndex}`} className="w-3 h-3"></div>;
                }
                
                const registrations = getRegistrationsForDate(day);
                const isCurrentMonth = isSameMonth(day, selectedDate);
                
                return (
                  <ContributionDay
                    key={day.toISOString()}
                    date={day}
                    registrations={registrations}
                    isCurrentMonth={isCurrentMonth}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      
      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 text-xs text-gray-600">
        <span>Less</span>
        <div className="flex gap-1">
          <div className="w-3 h-3 bg-gray-200 rounded-sm"></div>
          <div className="w-3 h-3 bg-green-200 rounded-sm"></div>
          <div className="w-3 h-3 bg-green-400 rounded-sm"></div>
          <div className="w-3 h-3 bg-green-600 rounded-sm"></div>
          <div className="w-3 h-3 bg-green-800 rounded-sm"></div>
        </div>
        <span>More</span>
      </div>
      
      {/* Stats */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Total Days:</span>
            <span className="font-semibold ml-2">
              {intensityData?.data?.totalDays || 0}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Total Registrations:</span>
            <span className="font-semibold ml-2">
              {intensityData?.data?.totalEngagements?.toLocaleString() || 0}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Average:</span>
            <span className="font-semibold ml-2">
              {intensityData?.data?.average || 0}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Best Day:</span>
            <span className="font-semibold ml-2">
              {intensityData?.data?.bestDay || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegistrationIntensity;